/*
 * NuviaSeed wishlist.
 *
 * The list lives in the visitor's browser (localStorage) so it works without an
 * account, an app, or a server round trip. Saved products are re-rendered on
 * /pages/wishlist through the Section Rendering API so the cards stay identical
 * to the ones on collection pages.
 */
(() => {
  const STORAGE_KEY = 'nuviaseed:wishlist:v1';
  const CHANGE_EVENT = 'wishlist:change';
  const MAX_ITEMS = 100;

  const rootUrl = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const read = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((item) => item && typeof item.handle === 'string' && item.handle.length);
    } catch (error) {
      // Private mode, disabled storage, or corrupted value: behave like an empty list.
      return [];
    }
  };

  const write = (items) => {
    const next = items.slice(0, MAX_ITEMS);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (error) {
      // Nothing to do; the in-memory result is still returned so the UI stays consistent.
    }
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items: next } }));
    return next;
  };

  const Wishlist = {
    storageKey: STORAGE_KEY,
    changeEvent: CHANGE_EVENT,
    items: read,
    count: () => read().length,
    has: (handle) => read().some((item) => item.handle === handle),
    add(item) {
      if (!item || !item.handle) return read();
      const items = read().filter((saved) => saved.handle !== item.handle);
      items.unshift({
        handle: item.handle,
        id: item.id || null,
        title: item.title || '',
        addedAt: Date.now(),
      });
      return write(items);
    },
    remove(handle) {
      return write(read().filter((item) => item.handle !== handle));
    },
    toggle(item) {
      if (!item || !item.handle) return false;
      if (this.has(item.handle)) {
        this.remove(item.handle);
        return false;
      }
      this.add(item);
      return true;
    },
    subscribe(callback) {
      document.addEventListener(CHANGE_EVENT, callback);
      return () => document.removeEventListener(CHANGE_EVENT, callback);
    },
  };

  window.NuviaWishlist = Wishlist;

  // Keep other tabs of the same store in sync.
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items: read() } }));
  });

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.control = this.querySelector('[data-wishlist-toggle]');
      if (!this.control) return;

      if (!this.bound) {
        this.control.addEventListener('click', (event) => {
          // Product cards wrap the whole tile in a link; don't follow it.
          event.preventDefault();
          event.stopPropagation();
          this.onToggle();
        });
        this.bound = true;
      }

      this.unsubscribe = Wishlist.subscribe(() => this.sync());
      this.sync();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    get productHandle() {
      return this.dataset.productHandle || '';
    }

    onToggle() {
      Wishlist.toggle({
        handle: this.productHandle,
        id: this.dataset.productId,
        title: this.dataset.productTitle,
      });
      this.sync();
    }

    sync() {
      const saved = Wishlist.has(this.productHandle);
      this.classList.toggle('is-active', saved);
      this.control.setAttribute('aria-pressed', saved ? 'true' : 'false');

      const label = saved ? this.dataset.labelSaved : this.dataset.labelSave;
      if (label) {
        this.control.setAttribute('aria-label', label);
        const text = this.querySelector('[data-wishlist-label]');
        if (text) text.textContent = label;
      }
    }
  }

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.unsubscribe = Wishlist.subscribe(() => this.sync());
      this.sync();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    sync() {
      const count = Wishlist.count();
      this.hidden = count === 0;
      this.textContent = count > 99 ? '99+' : String(count);
    }
  }

  class WishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.loading = this.querySelector('[data-wishlist-loading]');
      this.rendered = new Map();
      this.pending = new Set();

      this.render();
      this.unsubscribe = Wishlist.subscribe(() => this.render());
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    async render() {
      const handles = Wishlist.items().map((item) => item.handle);

      // Drop cards for products that were just removed.
      this.rendered.forEach((node, handle) => {
        if (handles.includes(handle)) return;
        node.remove();
        this.rendered.delete(handle);
      });

      const missing = handles.filter(
        (handle) => !this.rendered.has(handle) && !this.pending.has(handle)
      );
      if (missing.length) {
        if (this.loading) this.loading.hidden = false;
        await Promise.all(missing.map((handle) => this.addCard(handle)));
        if (this.loading) this.loading.hidden = true;
      }

      // Re-order the DOM so it matches the saved order (newest first).
      handles.forEach((handle) => {
        const node = this.rendered.get(handle);
        if (node) this.grid.appendChild(node);
      });

      this.toggleEmptyState();
    }

    async addCard(handle) {
      this.pending.add(handle);
      try {
        const response = await fetch(`${rootUrl()}products/${handle}?section_id=wishlist-card`);
        if (!response.ok) throw new Error(`Product ${handle} is unavailable`);

        const markup = await response.text();
        const card = new DOMParser()
          .parseFromString(markup, 'text/html')
          .querySelector('[data-wishlist-card]');
        if (!card) throw new Error(`No card markup for ${handle}`);

        const item = document.createElement('li');
        item.className = 'grid__item';
        item.dataset.wishlistItem = handle;
        item.appendChild(card);

        this.grid.appendChild(item);
        this.rendered.set(handle, item);
      } catch (error) {
        // The product was deleted, unpublished, or hidden from this market.
        Wishlist.remove(handle);
      } finally {
        this.pending.delete(handle);
      }
    }

    toggleEmptyState() {
      const isEmpty = this.rendered.size === 0;
      if (this.empty) this.empty.hidden = !isEmpty;
      if (this.grid) this.grid.hidden = isEmpty;
    }
  }

  if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
  if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
  if (!customElements.get('wishlist-page')) customElements.define('wishlist-page', WishlistPage);
})();
